import React, { useEffect, useState } from 'react'
import { Form, InputNumber, Checkbox, Row, Col, Select, Tooltip, Divider, DatePicker, Typography, Radio, Space } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'

import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { useShouldEnableFeatures } from '../../store/use-should-enable-features'
import { useEffectivePlan } from '../../store/use-effective-plan'
import { useAppSelector } from '../../store/hooks'
import {
  getPayoutDateForMonthlyPeriod,
  getPayoutDateForSemiMonthlyPeriod,
  getPayoutDateForBiWeeklyPeriod,
  getPayoutDateForWeeklyPeriod,
} from '@vacationtracker/shared/functions/calculation-shared'
import { getDatesForAccrualsSemiMonthly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-semi-monthly'
import { getDatesForAccrualsMonthly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-monthly'
import { getDatesForAccrualsBiWeekly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-bi-weekly'
import { getDatesForAccrualsWeekly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-weekly'
import { roundTo2Decimals } from '@vacationtracker/shared/functions/calculation-shared'
import { getShortestLeaveInterval } from '@vacationtracker/shared/functions/get-shortest-leave-interval'
import { getLeaveDaysNumber } from '@vacationtracker/shared/components/utils/leave-quotas'

import IntlMessages from '../../util/IntlMessages'

import { ILeavePolicyForm } from './types'
import { ILeavePolicy, RolloverStatus } from '../../types/leave-types'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { dayNames } from '@vacationtracker/shared/types/calendar'
import { numberOfDaysInMonth, generateDaysForMonth } from '../../util/number-of-days-in-month'
import { AccrualType, AccrualTypeEnum, ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import isNull from 'lodash/isNull'
import { getAverageWorkingHoursForLocation } from '@vacationtracker/shared/functions/work-week'
import { getQuotaInUnitsIfSet } from '@vacationtracker/shared/functions/get-days-or-hours'
import FreemiumPricingSimpleTooltip from '../freemium-pricing-simple-tooltip'

const { RangePicker } = DatePicker
const { Text } = Typography

const LeavePolicyForm = ({
  leavePolicy,
  form,
  copypolicy,
  location,
  showAccruals = false,
  yearStartDay = 1,
  showLeavePolicyTitle = false,
  isAssignLeavePolicyForm = false,
  isCreateLeaveTypeForm = false,
}: ILeavePolicyForm): React.ReactElement => {
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const hasToilFeatureFlag = featureFlags.includes(FeatureFlagEnum.toil)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const isFormInHours = Boolean(authCompany?.hourlyLeaveAccounting)
  const { formatMessage } = useIntl()
  const [quota, setQuota] = useState(getQuotaInUnitsIfSet(leavePolicy?.daysPerYear as number, leavePolicy?.hoursPerYear as number, isFormInHours, leavePolicy?.quotaSetIn) || 0)
  const [hasUnlimitedDays, setUnlimitedDays] = useState(leavePolicy?.hasUnlimitedDays || false)
  const [rolloverType, setRolloverType] = useState<RolloverStatus>(form.getFieldValue('rolloverType') as RolloverStatus)
  const [showAccrualsDate, setShowAccrualsDate] = useState(false)
  const [enableRolloverExpiry, setEnableRolloverExpiry] = useState(false)
  const [accrualType, setAccrualType] = useState('NONE')
  const [showApplyBroughtForwardSettingsToThePastYear, setShowApplyBroughtForwardSettingsToThePastYear] = useState(false)

  const [accrualCapRate, setAccrualCapRate] = useState(() => {
    if (leavePolicy?.accrualCapRate && leavePolicy?.accrualCapRate > 0) {
      form.setFieldsValue({ accrualCap: true })
      form.setFieldsValue({ accrualCapRate: leavePolicy?.accrualCapRate })
      return leavePolicy.accrualCapRate
    } else {
      form.setFieldsValue({ accrualCap: false })
    }
    return 0
  })
  const defaultAccrualCapRate = 2

  const [shouldAccrualsBeDisabled, setShouldAccrualsBeDisabled] = useState(false)
  const [shortestLeaveIntervals, setShortestLeaveIntervals] = useState([ShortestLeaveIntervalEnum.fullDay, ShortestLeaveIntervalEnum.halfDay])
  const [rolloverExpirySelectDays, setRolloverExpirySelectDays] = useState(
    generateDaysForMonth(numberOfDaysInMonth((leavePolicy?.rolloverExpiryMonth || location?.rolloverExpiryMonth) ?? 1))
  )
  const [step, setStep] = useState(
    leavePolicy?.shortestLeaveInterval === ShortestLeaveIntervalEnum.fullDay || leavePolicy?.shortestLeaveInterval == undefined || isFormInHours ? 1 : 0.5
  )
  const shouldEnableAccruals = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.accruals)
  const shouldEnableHourIntervals = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.shortestLeaveInterval)
  const shouldEnableLessThanOneHourLeave = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.lessThanOneHourLeave, false)

  const dateFormat = 'YYYY-MM-DD'

  const [toil, setToil] = useState(() => {
    const toil = Boolean(leavePolicy?.toil)
    form.setFieldsValue({ toil })
    if (toil) {
      form.setFieldsValue({
        toilShortestInterval: leavePolicy?.toilShortestInterval,
        toilRequestsAllowedForUsers: leavePolicy?.toilRequestsAllowedForUsers || false,
        toilExpiration: leavePolicy?.toilExpiration,
      })
      if (leavePolicy?.toilExpirationMonths && leavePolicy?.toilExpirationMonths > 0) {
        form.setFieldsValue({ toilExpirationMonths: leavePolicy.toilExpirationMonths })
      } else if (leavePolicy?.toilExpiration && leavePolicy.toilExpirationDate) {
        form.setFieldsValue({ toilExpirationMonths: 'calendar-year' })
      }
    }
    return toil
  })
  const [toilExpiration, setToilExpiration] = useState(() => {
    return Boolean(leavePolicy?.toilExpiration)
  })

  const months = [
    { label: 'app.january', value: 1 },
    { label: 'app.february', value: 2 },
    { label: 'app.march', value: 3 },
    { label: 'app.april', value: 4 },
    { label: 'app.may', value: 5 },
    { label: 'app.june', value: 6 },
    { label: 'app.july', value: 7 },
    { label: 'app.august', value: 8 },
    { label: 'app.september', value: 9 },
    { label: 'app.october', value: 10 },
    { label: 'app.november', value: 11 },
    { label: 'app.december', value: 12 },
  ]

  useEffect(() => {
    // Start with base intervals (FULL-DAY and HALF-DAY are already in the initial state)
    const newIntervals = [...shortestLeaveIntervals]
    // Add ONE-HOUR if hour intervals are enabled
    if (shouldEnableHourIntervals && !newIntervals.includes(ShortestLeaveIntervalEnum.oneHour)) {
      newIntervals.push(ShortestLeaveIntervalEnum.oneHour)
    }
    // Add HALF-HOUR and FIFTEEN-MINUTES if less-than-one-hour leave is enabled
    if (shouldEnableLessThanOneHourLeave) {
      if (!newIntervals.includes(ShortestLeaveIntervalEnum.halfHour)) {
        newIntervals.push(ShortestLeaveIntervalEnum.halfHour)
      }
      if (!newIntervals.includes(ShortestLeaveIntervalEnum.fifteenMinutes)) {
        newIntervals.push(ShortestLeaveIntervalEnum.fifteenMinutes)
      }
    }

    setShortestLeaveIntervals(newIntervals)
  }, [shouldEnableHourIntervals, shouldEnableLessThanOneHourLeave])

  useEffect(() => {
    setShouldAccrualsBeDisabled(Boolean(hasUnlimitedDays || !quota))
    form.setFieldsValue({ accrualCap: leavePolicy?.accrualCapRate !== 0 })
  }, [leavePolicy, hasUnlimitedDays, quota])

  useEffect(() => {
    if (leavePolicy?.shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour && !shouldEnableHourIntervals) {
      leavePolicy.shortestLeaveInterval = ShortestLeaveIntervalEnum.halfDay
      form.setFieldsValue({
        shortestLeaveInterval: ShortestLeaveIntervalEnum.halfDay,
      })
    }
  }, [shouldEnableHourIntervals, leavePolicy])

  useEffect(() => {
    if (copypolicy) {
      setShouldAccrualsBeDisabled(false)
      setUnlimitedDays(copypolicy?.hasUnlimitedDays || false)
      changeAccrualType(copypolicy.accrualType)
      if (copypolicy.toil) {
        setToil(Boolean(copypolicy.toil))
        setToilExpiration(Boolean(copypolicy?.toilExpiration))
      }

      let rolloverType: RolloverStatus
      if (copypolicy.maxRolloverDays === 366 || copypolicy.maxRolloverDays === 2928) {
        rolloverType = RolloverStatus.all
      } else if (copypolicy.maxRolloverDays > 0 || Boolean(copypolicy?.negativeBroughtForwardAllowed)) {
        rolloverType = RolloverStatus.limited
      } else {
        rolloverType = RolloverStatus.no
      }
      setRolloverType(rolloverType)

      // VT-12210: Handle rollover expiry settings from copypolicy
      const copiedEnableRolloverExpiry = copypolicy.broughtForwardExpirationSettings?.enabled ?? copypolicy.enableRolloverExpiry ?? false
      setEnableRolloverExpiry(copiedEnableRolloverExpiry)

      const quota = form.getFieldValue('quota')
      setQuota(quota as number)

      setTimeout(() => {
        // wait for form to be initialized
        setAccrualCapRate(copypolicy.accrualCapRate ? copypolicy.accrualCapRate : 0)

        // Set TOIL form fields when copying
        form.setFieldsValue({ toil: Boolean(copypolicy.toil) })
        if (copypolicy.toil) {
          form.setFieldsValue({
            toilShortestInterval: copypolicy?.toilShortestInterval,
            toilRequestsAllowedForUsers: copypolicy?.toilRequestsAllowedForUsers || false,
            toilExpiration: copypolicy?.toilExpiration,
          })
          if (copypolicy?.toilExpirationMonths && copypolicy?.toilExpirationMonths > 0) {
            form.setFieldsValue({ toilExpirationMonths: copypolicy.toilExpirationMonths })
          } else if (copypolicy?.toilExpiration && copypolicy.toilExpirationDate) {
            form.setFieldsValue({ toilExpirationMonths: 'calendar-year' })
          }
        }

        // VT-12210: Get rollover expiry date settings from broughtForwardExpirationSettings if available
        const rolloverExpiryMonth = copypolicy.broughtForwardExpirationSettings?.month ?? copypolicy.rolloverExpiryMonth
        const rolloverExpiryDay = copypolicy.broughtForwardExpirationSettings?.day ?? copypolicy.rolloverExpiryDay
        const rolloverExpiryAfterDays = copypolicy.broughtForwardExpirationSettings?.afterDays ?? copypolicy.rolloverExpiryAfterDays

        form.setFieldsValue({
          rolloverType,
          shortestLeaveInterval: getShortestLeaveInterval(copypolicy?.shortestLeaveInterval, copypolicy.allowHalfDays),
          accrualCap: copypolicy?.accrualCapRate && copypolicy?.accrualCapRate > 0 ? true : false,
          accrualCapRate: copypolicy?.accrualCapRate,
          negativeBroughtForwardAllowed: Boolean(copypolicy?.negativeBroughtForwardAllowed),
          enableRolloverExpiry: copiedEnableRolloverExpiry,
          rolloverExpiryMonth: rolloverExpiryMonth,
          rolloverExpiryDay: rolloverExpiryDay,
          rolloverExpiryAfterDays: rolloverExpiryAfterDays,
        })
      }, 0)
    }
  }, [copypolicy])

  useEffect(() => {
    const accrualType = form.getFieldValue('accrualType')
    if (accrualType !== 'NONE') {
      changeAccrualType(accrualType as AccrualType)
    }
  }, [form])

  useEffect(() => {
    // VT-12210: Only set these values from leavePolicy/location if we're NOT copying a policy
    // When copying, the [copypolicy] useEffect and the modal handle this
    if (!copypolicy) {
      if (typeof leavePolicy?.broughtForwardExpirationSettings?.enabled === 'boolean') {
        form.setFieldsValue({
          enableRolloverExpiry: leavePolicy?.broughtForwardExpirationSettings.enabled,
        })
        setEnableRolloverExpiry(leavePolicy?.broughtForwardExpirationSettings.enabled)
      } else if (typeof location?.rolloverNeverExpireDays === 'boolean') {
        setEnableRolloverExpiry(!location?.rolloverNeverExpireDays)
      } else {
        setEnableRolloverExpiry(false)
      }

      if (authCompany?.hourlyLeaveAccounting && leavePolicy?.quotaSetIn === 'hours') {
        form.setFieldsValue({ quota: leavePolicy?.hoursPerYear })
        form.setFieldsValue({ maxRollover: leavePolicy?.maxRolloverHours })
      } else if (authCompany?.hourlyLeaveAccounting && leavePolicy?.quotaSetIn === 'days') {
        form.setFieldsValue({ quota: leavePolicy?.hoursPerYear ?? leavePolicy.daysPerYear * getAverageWorkingHoursForLocation(location?.workHours) })
        form.setFieldsValue({ maxRollover: leavePolicy?.maxRolloverHours ?? leavePolicy.maxRolloverDays * getAverageWorkingHoursForLocation(location?.workHours) })
      } else {
        // probably days
        form.setFieldsValue({ quota: leavePolicy?.daysPerYear })
        form.setFieldsValue({ maxRollover: leavePolicy?.maxRolloverDays })
      }
    }
  }, [leavePolicy, location, copypolicy])

  useEffect(() => {
    if (showApplyBroughtForwardSettingsToThePastYear) {
      form.setFieldsValue({
        applyBroughtForwardSettingsToThePastYear: false,
      })
    }
  }, [showApplyBroughtForwardSettingsToThePastYear])

  const onChangeMonthExpiryDay = (month: number) => {
    setShowApplyBroughtForwardSettingsToThePastYear(true)
    const numberOfDays = numberOfDaysInMonth(month)
    const days = generateDaysForMonth(numberOfDays)
    setRolloverExpirySelectDays(days)
  }

  const onChangeRolloverExpiryAfterDays = (days: number) => {
    form.setFieldsValue({
      rolloverExpiryAfterDays: Math.round(days),
    })
  }

  const onChangeNegativeBroughtForward = () => {
    setShowApplyBroughtForwardSettingsToThePastYear(true)
  }

  const onChangeHandleRolloverExpiry = (value: boolean) => {
    setShowApplyBroughtForwardSettingsToThePastYear(true)
    setEnableRolloverExpiry(value)

    form.setFieldsValue({
      enableRolloverExpiry: value,
    })
  }

  const onToggleToil = (e) => {
    const toilEnabled = e.target.value as boolean
    setToil(toilEnabled)
    form.setFieldsValue({ toil: toilEnabled })
    if (toilEnabled) {
      form.setFieldsValue({
        toilShortestInterval: leavePolicy?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay,
        toilExpiration: false,
        toilRequestsAllowedForUsers: false,
      })
    }
  }

  const onToggleExpiration = (e) => {
    const expirationEnabled = e.target.value as boolean
    setToilExpiration(expirationEnabled)
    form.setFieldsValue({ toilExpiration: expirationEnabled })
    if (expirationEnabled) {
      form.setFieldsValue({ toilExpirationMonths: 1 })
    }
  }

  const getDayWithSuffix = (d) => {
    if (d > 3 && d < 21) {
      return `${d}th`
    }
    switch (d % 10) {
      case 1: {
        return `${d}st`
      }
      case 2: {
        return `${d}nd`
      }
      case 3: {
        return `${d}rd`
      }
      default: {
        return `${d}th`
      }
    }
  }

  const getAccrualDaysMessage = () => {
    const earningDate = form.getFieldValue('firstEarningDate') as Date

    if (!earningDate) {
      return ''
    }

    if (accrualType !== 'NONE') {
      switch (accrualType) {
        case AccrualTypeEnum.monthly:
          return (
            <IntlMessages
              id={isFormInHours ? 'components.leavePolicyForm.accrualMonthlySummaryHours' : 'components.leavePolicyForm.accrualMonthlySummary'}
              values={{
                amount: roundTo2Decimals(form.getFieldValue('quota') / 12),
                quota: form.getFieldValue('quota'),
                date: getDayWithSuffix(dayjs(form.getFieldValue('firstEarningDate') as Date).date()),
              }}
            />
          )
        case AccrualTypeEnum.semiMonthly: {
          let firstDate: string, secondDate: string

          if (dayjs(earningDate).date() >= 16) {
            firstDate = getDayWithSuffix(dayjs(earningDate).subtract(15, 'days').date())
            secondDate = getDayWithSuffix(dayjs(earningDate).date())
          } else {
            firstDate = getDayWithSuffix(dayjs(earningDate).date())
            secondDate = getDayWithSuffix(dayjs(earningDate).add(15, 'days').date())
          }

          return (
            <IntlMessages
              id={isFormInHours ? 'components.leavePolicyForm.accrualSemiMonthlySummaryHours' : 'components.leavePolicyForm.accrualSemiMonthlySummary'}
              values={{
                amount: roundTo2Decimals(form.getFieldValue('quota') / 24),
                quota: form.getFieldValue('quota'),
                firstDate,
                secondDate,
              }}
            />
          )
        }
        case AccrualTypeEnum.biWeekly:
          return (
            <IntlMessages
              id={isFormInHours ? 'components.leavePolicyForm.accrualBiWeeklySummaryHours' : 'components.leavePolicyForm.accrualBiWeeklySummary'}
              values={{
                amount: roundTo2Decimals(form.getFieldValue('quota') / 26),
                quota: form.getFieldValue('quota'),
                day: formatMessage({ id: dayNames[form.getFieldValue('firstEarningDate').day()] }),
              }}
            />
          )
        case AccrualTypeEnum.weekly:
          return (
            <IntlMessages
              id={isFormInHours ? 'components.leavePolicyForm.accrualWeeklySummaryHours' : 'components.leavePolicyForm.accrualWeeklySummary'}
              values={{
                amount: roundTo2Decimals(form.getFieldValue('quota') / 52),
                quota: form.getFieldValue('quota'),
                day: formatMessage({ id: dayNames[form.getFieldValue('firstEarningDate').day()] }),
              }}
            />
          )
        default:
          return null
      }
    } else {
      return null
    }
  }

  const accrualTypes = [
    { type: AccrualTypeEnum.none },
    { type: AccrualTypeEnum.monthly },
    { type: AccrualTypeEnum.semiMonthly },
    { type: AccrualTypeEnum.biWeekly },
    { type: AccrualTypeEnum.weekly },
  ]

  const formItemLayout = {
    labelCol: { span: 17, offset: 0 },
    wrapperCol: { span: 7, offset: 0 },
  }
  const formFullWidthLayout = {
    labelCol: { span: 11, offset: 0 },
    wrapperCol: { span: 11, offset: 0 },
  }

  const changeRolloveType = (type: RolloverStatus) => {
    setRolloverType(type)
    setShowApplyBroughtForwardSettingsToThePastYear(true)
    form.setFieldsValue({
      applyBroughtForwardSettingsToThePastYear: false,
    })
  }

  const onQuotaChange = (newQuota: number): void => {
    const quota = getLeaveDaysNumber(accrualType, leavePolicy?.allowHalfDays, form.getFieldValue('shortestLeaveInterval') as string, newQuota, isFormInHours)

    // if we track leaves in hours then we need to prevent decimal values
    const value = isFormInHours ? Math.trunc(quota) : quota

    form.setFieldsValue({
      quota: value,
    })

    setQuota(value)

    if (!newQuota) {
      resetAccrualState(null)
      return
    }
  }

  const onChangeMaxRollover = (amount: number): void => {
    setShowApplyBroughtForwardSettingsToThePastYear(true)
    const maxRollover = getLeaveDaysNumber(accrualType, leavePolicy?.allowHalfDays, form.getFieldValue('shortestLeaveInterval') as string, amount, isFormInHours)
    // if we track leaves in hours then we need to prevent decimal values
    const value = isFormInHours ? Math.trunc(maxRollover) : maxRollover
    form.setFieldsValue({
      maxRollover: value,
    })
  }

  const onChangeAccrualCap = (event): void => {
    const value = Boolean(event.target.value)
    if (value) {
      form.setFieldsValue({ rolloverType: RolloverStatus.all, enableRolloverExpiry: false, accrualCapRate: defaultAccrualCapRate })
      setRolloverType(RolloverStatus.all)
      setEnableRolloverExpiry(false)
      setAccrualCapRate(defaultAccrualCapRate)
    } else {
      form.setFieldsValue({ accrualCapRate: 0 })
      setAccrualCapRate(0)
    }
  }

  const disabledAcrrualsDate = (checkDate: Dayjs): boolean => {
    const today = new Date()
    const checkDateString = checkDate.format(dateFormat)

    if (accrualType === AccrualTypeEnum.monthly) {
      const { pickerEndDate, pickerStartDate } = getDatesForAccrualsMonthly({ today, yearStartDay })
      return checkDateString < pickerStartDate || checkDateString > pickerEndDate
    }
    if (accrualType === AccrualTypeEnum.semiMonthly) {
      const { pickerStartDate, pickerEndDate } = getDatesForAccrualsSemiMonthly({ today, yearStartDay })
      return checkDateString < pickerStartDate || checkDateString > pickerEndDate
    }
    if (accrualType === AccrualTypeEnum.biWeekly) {
      const accrualPeriodStart = form.getFieldValue('accrualPeriod')?.[0]?.format('YYYY-MM-DD')
      const { pickerStartDate, pickerEndDate } = getDatesForAccrualsBiWeekly({ today, accrualPeriodStart })
      return checkDateString < pickerStartDate || checkDateString > pickerEndDate
    }
    if (accrualType === AccrualTypeEnum.weekly) {
      const accrualPeriodStart = form.getFieldValue('accrualPeriod')?.[0]?.format('YYYY-MM-DD')
      const { pickerStartDate, pickerEndDate } = getDatesForAccrualsWeekly({ today, accrualPeriodStart })
      return checkDateString < pickerStartDate || checkDateString > pickerEndDate
    }

    return false
  }

  const changeAccrualType = (newAccrualType: AccrualType): void => {
    const today = new Date()

    if (newAccrualType === AccrualTypeEnum.monthly) {
      const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsMonthly({ today, yearStartDay })

      // Use copypolicy value if copying, otherwise use leavePolicy value
      const pauseAccrualsValue = copypolicy
        ? typeof copypolicy.pauseAccrualsWhenUserIsDeactivated === 'undefined'
          ? true
          : copypolicy.pauseAccrualsWhenUserIsDeactivated
        : isNull(leavePolicy?.pauseAccrualsWhenUserIsDeactivated) || leavePolicy?.pauseAccrualsWhenUserIsDeactivated

      form.setFieldsValue({
        accrualPeriod: [dayjs(accrualPeriodStart), dayjs(accrualPeriodEnd)],
        pauseAccrualsWhenUserIsDeactivated: pauseAccrualsValue,
      })

      if (leavePolicy?.firstEarningDate && leavePolicy.accrualType === AccrualTypeEnum.monthly) {
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForMonthlyPeriod(accrualPeriodEnd, leavePolicy.firstEarningDate, leavePolicy?.accrualPeriodStart)),
        })
      } else if (copypolicy?.firstEarningDate) {
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForMonthlyPeriod(accrualPeriodEnd, copypolicy.firstEarningDate, copypolicy?.accrualPeriodStart)),
        })
      } else {
        form.setFieldsValue({
          firstEarningDate: dayjs(accrualPeriodEnd).add(1, 'days'),
        })
      }
    }

    if (newAccrualType === AccrualTypeEnum.semiMonthly) {
      const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsSemiMonthly({ today, yearStartDay })

      form.setFieldsValue({
        accrualPeriod: [dayjs(accrualPeriodStart), dayjs(accrualPeriodEnd)],
      })

      if (leavePolicy?.firstEarningDate && leavePolicy.accrualType === AccrualTypeEnum.semiMonthly) {
        form.setFieldsValue({
          firstEarningDate: dayjs(
            getPayoutDateForSemiMonthlyPeriod({
              periodStart: accrualPeriodStart,
              periodEnd: accrualPeriodEnd,
              firstEarningDate: leavePolicy.firstEarningDate,
              accrualPeriodStart: leavePolicy?.accrualPeriodStart,
            })
          ),
        })
      } else if (copypolicy?.firstEarningDate) {
        form.setFieldsValue({
          firstEarningDate: dayjs(
            getPayoutDateForSemiMonthlyPeriod({
              periodStart: accrualPeriodStart,
              periodEnd: accrualPeriodEnd,
              firstEarningDate: copypolicy.firstEarningDate,
              accrualPeriodStart: copypolicy?.accrualPeriodStart,
            })
          ),
        })
      } else {
        form.setFieldsValue({
          firstEarningDate: dayjs(accrualPeriodEnd),
        })
      }
    }

    if (newAccrualType === AccrualTypeEnum.biWeekly) {
      const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsBiWeekly({ today, accrualPeriodStart: leavePolicy?.accrualPeriodStart })

      form.setFieldsValue({
        accrualPeriod: [dayjs(accrualPeriodStart), dayjs(accrualPeriodEnd)],
      })

      if (leavePolicy?.accrualPeriodStart && leavePolicy?.firstEarningDate && leavePolicy.accrualType === AccrualTypeEnum.biWeekly) {
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForBiWeeklyPeriod(accrualPeriodStart, accrualPeriodEnd, leavePolicy.firstEarningDate, leavePolicy?.accrualPeriodStart)),
        })
      } else if (copypolicy?.firstEarningDate) {
        const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsBiWeekly({ today, accrualPeriodStart: copypolicy?.accrualPeriodStart })
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForBiWeeklyPeriod(accrualPeriodStart, accrualPeriodEnd, copypolicy.firstEarningDate, copypolicy?.accrualPeriodStart)),
        })
      } else {
        form.setFieldsValue({
          firstEarningDate: dayjs(accrualPeriodEnd),
        })
      }
    }

    if (newAccrualType === AccrualTypeEnum.weekly) {
      const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsWeekly({ today, accrualPeriodStart: leavePolicy?.accrualPeriodStart })

      form.setFieldsValue({
        accrualPeriod: [dayjs(accrualPeriodStart), dayjs(accrualPeriodEnd)],
      })

      if (leavePolicy?.accrualPeriodStart && leavePolicy?.firstEarningDate && leavePolicy.accrualType === AccrualTypeEnum.weekly) {
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForWeeklyPeriod(accrualPeriodStart, accrualPeriodEnd, leavePolicy.firstEarningDate, leavePolicy.accrualPeriodStart)),
        })
      } else if (copypolicy?.firstEarningDate) {
        const { accrualPeriodStart, accrualPeriodEnd } = getDatesForAccrualsWeekly({ today, accrualPeriodStart: copypolicy?.accrualPeriodStart })
        form.setFieldsValue({
          firstEarningDate: dayjs(getPayoutDateForWeeklyPeriod(accrualPeriodStart, accrualPeriodEnd, copypolicy.firstEarningDate, copypolicy.accrualPeriodStart)),
        })
      } else {
        form.setFieldsValue({
          firstEarningDate: dayjs(accrualPeriodEnd),
        })
      }
    }

    if (newAccrualType === 'NONE') {
      setShowAccrualsDate(false)
    } else {
      setShowAccrualsDate(true)
    }

    setAccrualType(newAccrualType)
  }

  const resetAccrualState = (leavePolicy?: ILeavePolicy | null) => {
    if (leavePolicy) {
      changeAccrualType(leavePolicy?.accrualType)
      form.setFieldsValue({
        accrualType: leavePolicy?.accrualType,
      })
    } else {
      changeAccrualType('NONE')
      form.setFieldsValue({
        accrualType: 'NONE',
        accrualPeriod: [dayjs(), dayjs().add(13, 'days')],
        firstEarningDate: null,
      })
    }
  }

  const onChangeBiWeeklyPeriod = (_: unknown, dates: [string, string]) => {
    form.setFieldsValue({
      accrualPeriod: [dayjs(dates[0]), dayjs(dates[0]).add(13, 'days')],
      firstEarningDate: dayjs(dayjs(dates[0]).add(13, 'days')),
    })
  }

  const onChangeWeeklyPeriod = (_: unknown, dates: [string, string]) => {
    form.setFieldsValue({
      accrualPeriod: [dayjs(dates[0]), dayjs(dates[0]).add(6, 'days')],
      firstEarningDate: dayjs(dayjs(dates[0]).add(6, 'days')),
    })
  }

  const changeShortestLeaveDuration = () => {
    const shortestLeaveInterval = form.getFieldValue('shortestLeaveInterval')
    const maxRollover = form.getFieldValue('maxRollover')
    const quota = form.getFieldValue('quota')

    if (!shouldEnableHourIntervals && shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour) {
      form.setFieldsValue({ shortestLeaveInterval: ShortestLeaveIntervalEnum.halfDay })
    }

    if (!shouldEnableLessThanOneHourLeave && (shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour || shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes)) {
      // If hour intervals are enabled, fall back to ONE-HOUR, otherwise HALF-DAY
      form.setFieldsValue({
        shortestLeaveInterval: shouldEnableHourIntervals ? ShortestLeaveIntervalEnum.oneHour : ShortestLeaveIntervalEnum.halfDay,
      })
    }

    if (shortestLeaveInterval === ShortestLeaveIntervalEnum.fullDay || shortestLeaveInterval === undefined) {
      form.setFieldsValue({ maxRollover: Math.trunc(maxRollover as number) })
      form.setFieldsValue({ quota: Math.trunc(quota as number) })
      setStep(1)
    } else {
      setStep(0.5)
    }
  }

  return (
    <>
      {showLeavePolicyTitle && (
        <Divider>
          <IntlMessages id="components.leavePolicyForm.leavePolicy" />
        </Divider>
      )}

      <Row>
        <Col span={14}>
          <Form.Item className="wrap-label" label={<IntlMessages id="app.approvalRequired" />} name="isApprovalRequired" valuePropName="checked" {...formItemLayout}>
            <Checkbox />
          </Form.Item>

          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="components.leavePolicyForm.hasUnlimitedDays" />}
            name="hasUnlimitedDays"
            valuePropName="checked"
            {...formItemLayout}
          >
            <Checkbox
              onChange={(e) => {
                if (e.target.checked) {
                  form.setFieldsValue({ quota: 0 })
                }
                setUnlimitedDays(!hasUnlimitedDays)
                if (accrualType === 'NONE') {
                  resetAccrualState(null)
                  return
                }
                if (leavePolicy?.accrualType === 'NONE' || e.target.checked || !form.getFieldValue('quota')) {
                  resetAccrualState(null)
                } else {
                  resetAccrualState(leavePolicy)
                }
              }}
            />
          </Form.Item>

          <Form.Item className="wrap-label" label={<IntlMessages id="app.shortestLeaveDuration" />} name="shortestLeaveInterval" {...formItemLayout}>
            <Select key="shortestLeaveInterval" popupMatchSelectWidth={false} onChange={changeShortestLeaveDuration}>
              {shortestLeaveIntervals.map((period) => (
                <Select.Option key={period} value={period}>
                  <IntlMessages id={`app.${period.toLowerCase()}`} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <>
                <IntlMessages id="components.leavePolicyForm.allowAddAndEditLeaveInPast" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.allowAddAndEditLeaveInPastInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            className="form-item-label-pre-wrap wrap-label"
            name="allowLeaveInPast"
            valuePropName="checked"
            {...formItemLayout}
          >
            <Checkbox />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.leavePolicyForm.negativeBalance" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.negativeBalanceInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="negativeBallanceAllowed"
            valuePropName="checked"
            {...formItemLayout}
          >
            <Checkbox disabled={hasUnlimitedDays} />
          </Form.Item>
          <Form.Item
            className="wrap-label"
            style={{ width: 150 }}
            label={<IntlMessages id="components.leavePolicyForm.yearlyQuota" />}
            name="quota"
            {...formItemLayout}
            rules={[{ required: !hasUnlimitedDays, message: <IntlMessages id="components.leavePolicyForm.required" /> }]}
          >
            <InputNumber
              step={step}
              addonAfter={<IntlMessages id={isFormInHours ? 'general.hours' : 'general.days'} />}
              min={0}
              max={isFormInHours ? 2920 : 365}
              style={{ width: 140 }}
              onChange={(value) => onQuotaChange(value as number)}
              disabled={hasUnlimitedDays}
              data-qa="quota"
            />
          </Form.Item>

          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="components.leavePolicyForm.reasonRequired" />}
            name="isReasonRequired"
            valuePropName="checked"
            {...formItemLayout}
          >
            <Checkbox />
          </Form.Item>

          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.leavePolicyForm.hideLeaveType" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.hideLeaveTypeInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="hideLeaveType"
            valuePropName="checked"
            {...formItemLayout}
          >
            <Checkbox />
          </Form.Item>
        </Col>
      </Row>

      {!isCreateLeaveTypeForm && (
        <>
          <Divider>
            <IntlMessages id="app.accruals" />
          </Divider>
          <Form.Item
            className="wrap-label"
            key="accrualTypeWrap"
            label={
              <>
                <IntlMessages id="components.leavePolicyForm.accrualType" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.accrualTypeInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            {...formFullWidthLayout}
            shouldUpdate
          >
            {() => {
              return (
                <>
                  <Form.Item key="accrualType" name="accrualType" style={{ marginBottom: '0px' }}>
                    <Select
                      key="accrualTypeSelect"
                      onChange={changeAccrualType}
                      options={accrualTypes.map((event) => {
                        // Check if disabled because user doesn't have access to accruals feature
                        const disabledDueToLackOfPlan = !shouldEnableAccruals || authCompany?.plan === SubscriptionPlanEnum.freemium

                        // Check if disabled because quota is unlimited or 0 (only relevant if user has access)
                        const disabledDueToQuota = shouldEnableAccruals && (hasUnlimitedDays || !quota)

                        // Disable if either condition is true, but 'NONE' is always enabled
                        const disabled = event.type === 'NONE' ? false : disabledDueToLackOfPlan || disabledDueToQuota

                        return {
                          value: event.type,
                          key: event.type,
                          disabled,
                          // Only show "Complete plan needed" message when disabled due to lack of plan, NOT due to quota
                          label: (
                            <>
                              {<IntlMessages id={`app.${event.type.toLowerCase()}`} />}
                              {disabledDueToLackOfPlan && event.type !== 'NONE' && (
                                <>
                                  &nbsp;
                                  <Text code>
                                    <IntlMessages id="app.feature.access" />
                                  </Text>
                                </>
                              )}
                            </>
                          ),
                        }
                      })}
                    />
                  </Form.Item>
                </>
              )
            }}
          </Form.Item>
          {(hasUnlimitedDays || quota === 0) && (
            <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: 'center' }} shouldUpdate>
              {() => {
                return (
                  <Text type="secondary">
                    <IntlMessages id="components.leavePolicyForm.accrualDisabledInfo" />
                  </Text>
                )
              }}
            </Form.Item>
          )}
        </>
      )}

      {showAccrualsDate && (accrualType === AccrualTypeEnum.monthly || accrualType === AccrualTypeEnum.semiMonthly) && (
        <Form.Item name="accrualPeriod" label={<IntlMessages id="components.leavePolicyForm.currentAccrualPeriod" />} {...formFullWidthLayout}>
          <RangePicker format={dateFormat} disabled={true} />
        </Form.Item>
      )}
      {showAccrualsDate && accrualType === 'BI-WEEKLY' && (
        <Form.Item
          className="wrap-label"
          name="accrualPeriod"
          label={<IntlMessages id="components.leavePolicyForm.currentAccrualPeriod" />}
          rules={[{ required: true, message: <IntlMessages id="components.leavePolicyForm.required" /> }]}
          {...formFullWidthLayout}
        >
          <RangePicker
            allowClear={false}
            format={dateFormat}
            disabledDate={(checkDate) => {
              return checkDate < dayjs().subtract(13, 'days') || checkDate > dayjs()
            }}
            disabled={[shouldAccrualsBeDisabled, true]}
            onCalendarChange={onChangeBiWeeklyPeriod}
          />
        </Form.Item>
      )}
      {showAccrualsDate && accrualType === 'WEEKLY' && (
        <Form.Item
          className="wrap-label"
          name="accrualPeriod"
          label={<IntlMessages id="components.leavePolicyForm.currentAccrualPeriod" />}
          rules={[{ required: true, message: <IntlMessages id="components.leavePolicyForm.required" /> }]}
          {...formFullWidthLayout}
        >
          <RangePicker
            allowClear={false}
            format={dateFormat}
            disabledDate={(checkDate) => {
              return checkDate < dayjs().subtract(6, 'days') || checkDate > dayjs()
            }}
            disabled={[shouldAccrualsBeDisabled, true]}
            onCalendarChange={onChangeWeeklyPeriod}
          />
        </Form.Item>
      )}
      {showAccrualsDate && (
        <>
          <Form.Item
            className="wrap-label"
            label={<IntlMessages id="components.leavePolicyForm.nextAccrualDay" />}
            name="firstEarningDate"
            rules={[{ required: true, message: <IntlMessages id="components.leavePolicyForm.required" /> }]}
            {...formFullWidthLayout}
          >
            <DatePicker format={dateFormat} disabledDate={disabledAcrrualsDate} disabled={shouldAccrualsBeDisabled} />
          </Form.Item>
          <Form.Item wrapperCol={{ span: 20, offset: 4 }} shouldUpdate>
            {() => {
              return (
                <p className="vt-primary-color" style={{ textAlign: 'center', margin: 0 }}>
                  {getAccrualDaysMessage()}
                </p>
              )
            }}
          </Form.Item>
        </>
      )}
      {showAccrualsDate && (
        <Form.Item
          className="wrap-label"
          label={
            <>
              <IntlMessages id="components.leavePolicyForm.allowAdvanceAccrualUsage" />
              <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.allowAdvanceAccrualUsageInfo" />}>
                <InfoCircleOutlined />
              </Tooltip>
            </>
          }
          name="allowAdvanceAccrualUsage"
          {...formFullWidthLayout}
        >
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={true}>
              <IntlMessages id="app.yes" />
            </Radio.Button>
            <Radio.Button value={false}>
              <IntlMessages id="app.no" />
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      )}
      {showAccrualsDate && (
        <Form.Item
          className="wrap-label"
          label={
            <>
              <IntlMessages id="components.leavePolicyForm.pauseAccrualsWhenUserIsDeactivated" />
              <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.pauseAccrualsWhenUserIsDeactivatedInfo" />}>
                <InfoCircleOutlined />
              </Tooltip>
            </>
          }
          name="pauseAccrualsWhenUserIsDeactivated"
          {...formFullWidthLayout}
        >
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={true}>
              <IntlMessages id="app.yes" />
            </Radio.Button>
            <Radio.Button value={false}>
              <IntlMessages id="app.no" />
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      )}
      {showAccrualsDate && (
        <>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.leavePolicyForm.accrualCap" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.accrualCapTooltip" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="accrualCap"
            {...formFullWidthLayout}
          >
            <Radio.Group buttonStyle="solid" onChange={onChangeAccrualCap}>
              <Radio.Button value={true}>
                <IntlMessages id="app.yes" />
              </Radio.Button>
              <Radio.Button value={false}>
                <IntlMessages id="app.no" />
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          {accrualCapRate > 0 && (
            <Form.Item label={<IntlMessages id="components.leavePolicyForm.accrualCapRate" />} {...formFullWidthLayout}>
              <Space>
                <Form.Item className="wrap-label" name="accrualCapRate" style={{ marginBottom: '0px' }} {...formFullWidthLayout}>
                  <InputNumber
                    min={0.1}
                    max={10}
                    defaultValue={2}
                    precision={2}
                    step={0.01}
                    value={accrualCapRate}
                    onChange={(val: number | null) => {
                      if (val !== null) {
                        setAccrualCapRate(val)
                        form.setFieldsValue({ accrualCapRate: val })
                      }
                    }}
                  />
                </Form.Item>
                <Typography>
                  <IntlMessages id="components.leavePolicyForm.accrualCapRateTimes" />
                </Typography>
              </Space>
            </Form.Item>
          )}
        </>
      )}

      {isCreateLeaveTypeForm && (
        <div>
          <Divider style={{ opacity: 0.5 }}>
            <IntlMessages id="app.accruals" />
          </Divider>
          <Form.Item className="wrap-label" key="accrualTypeWrap" label={<IntlMessages id="components.leavePolicyForm.accrualType" />} style={{ opacity: 0.5 }}>
            <Select value={<IntlMessages id={'app.none'} />} disabled>
              <Select.Option>
                <IntlMessages id={'app.none'} />
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item wrapperCol={{ span: 24 }}>
            <Text type="secondary">
              <IntlMessages id="components.leavePolicyForm.createLeaveTypeFormAccrualsInfo1" />
              &nbsp;{effectivePlan === SubscriptionPlanEnum.core && <IntlMessages id="app.feature.access" />}
            </Text>
          </Form.Item>
        </div>
      )}

      <Divider>
        <IntlMessages id="components.leavePolicyForm.rolloverSettings" />
      </Divider>

      <Form.Item
        {...formFullWidthLayout}
        className="wrap-label"
        label={
          <>
            <IntlMessages id="app.broughtForward" />
            <Tooltip
              className="info-tooltip"
              title={
                isFormInHours ? <IntlMessages id="components.leavePolicyForm.maxRolloverDaysInfoHours" /> : <IntlMessages id="components.leavePolicyForm.maxRolloverDaysInfo" />
              }
            >
              <InfoCircleOutlined />
            </Tooltip>
          </>
        }
        name="rolloverType"
      >
        <Select onSelect={changeRolloveType} value={rolloverType} disabled={hasUnlimitedDays || accrualCapRate > 0}>
          <Select.Option key="no" value={RolloverStatus.no}>
            <IntlMessages id="components.leavePolicyForm.noRollover" />
          </Select.Option>
          <Select.Option key="limited" value={RolloverStatus.limited}>
            <IntlMessages id="components.leavePolicyForm.limitedNumberOfDays" />
          </Select.Option>
          <Select.Option key="all" value={RolloverStatus.all}>
            <IntlMessages id="components.leavePolicyForm.allDays" />
          </Select.Option>
        </Select>
      </Form.Item>
      {!hasUnlimitedDays && rolloverType === 'limited' && (
        <Form.Item
          {...formFullWidthLayout}
          className="wrap-label"
          label={<IntlMessages id="components.leavePolicyForm.rollover" />}
          name="maxRollover"
          rules={[{ required: true, min: 1, type: 'number', message: <IntlMessages id="components.leavePolicyForm.required" /> }]}
        >
          <InputNumber<number>
            min={1}
            addonAfter={<IntlMessages id={isFormInHours ? 'general.hours' : 'general.days'} />}
            max={isFormInHours ? 2920 : 365}
            step={step}
            disabled={hasUnlimitedDays}
            onChange={(value) => onChangeMaxRollover(value as number)}
          />
        </Form.Item>
      )}
      {!hasUnlimitedDays && rolloverType !== 'no' && location && (
        <>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id={isFormInHours ? 'location.rolloverExpiryHour' : 'location.rolloverExpiryDay'} />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.locationForm.rolloverExpiryDayInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            valuePropName="checked"
            name="enableRolloverExpiry"
            {...formFullWidthLayout}
          >
            <Radio.Group
              disabled={accrualCapRate > 0}
              value={enableRolloverExpiry}
              buttonStyle="solid"
              options={[
                { label: <IntlMessages id="app.yes" />, value: true },
                { label: <IntlMessages id="app.no" />, value: false },
              ]}
              optionType="button"
              onChange={(e) => onChangeHandleRolloverExpiry(e.target.value as boolean)}
            />
          </Form.Item>
          {enableRolloverExpiry && location?.resetQuotas === 'FISCAL_YEAR' && (
            <Form.Item
              className="wrap-label"
              {...formFullWidthLayout}
              label={<IntlMessages id={isFormInHours ? 'location.rolloverExpiryOnDateHours' : 'location.rolloverExpiryOnDate'} />}
              style={{ marginBottom: 24 }}
              extra={<IntlMessages id={isFormInHours ? 'location.rolloverExpiryOnDateHours.extra' : 'location.rolloverExpiryOnDate.extra'} />}
            >
              <Form.Item
                name="rolloverExpiryMonth"
                style={{ display: 'inline-block', width: 140, marginBottom: 0 }}
                rules={[
                  { required: true, message: <IntlMessages id="form.inputRequired" /> },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const numberOfDays = numberOfDaysInMonth(value as number)
                      const selectedExpiryDay = getFieldValue('rolloverExpiryDay')
                      if (selectedExpiryDay <= numberOfDays) {
                        return Promise.resolve()
                      }
                      form.setFieldsValue({ rolloverExpiryDay: 1 })
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Select<number> style={{ width: 130, marginRight: 10 }} onChange={(month) => onChangeMonthExpiryDay(month)}>
                  {months.map((month) => (
                    <Select.Option key={month.value} value={month.value}>
                      <IntlMessages id={month.label} />
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="rolloverExpiryDay"
                style={{ display: 'inline-block', width: 80, marginBottom: 0 }}
                rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
              >
                <Select<number> style={{ width: 80 }} onChange={() => setShowApplyBroughtForwardSettingsToThePastYear(true)}>
                  {rolloverExpirySelectDays.map((day) => (
                    <Select.Option key={day.value} value={day.value}>
                      {day.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form.Item>
          )}
        </>
      )}
      {!isAssignLeavePolicyForm && location?.resetQuotas === 'FISCAL_YEAR' && accrualCapRate === 0 && showApplyBroughtForwardSettingsToThePastYear && (
        <Form.Item
          name="applyBroughtForwardSettingsToThePastYear"
          rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
          label={
            <>
              <IntlMessages id="components.leavePolicyForm.whenWouldYouLikeToApplyTheseChanges" />
              <Tooltip className="info-tooltip" title={<IntlMessages id="components.leavePolicyForm.whenWouldYouLikeToApplyTheseChangesInfo" />}>
                <InfoCircleOutlined />
              </Tooltip>
            </>
          }
          {...formFullWidthLayout}
        >
          <Select style={{ width: 320 }}>
            <Select.Option key="false" value={false}>
              <IntlMessages id="components.leavePolicyForm.futureBroughtForward" />
            </Select.Option>
            <Select.Option key="true" value={true}>
              <IntlMessages id="components.leavePolicyForm.previouesBroughForwardAndFutureOnce" />
            </Select.Option>
          </Select>
        </Form.Item>
      )}
      {!hasUnlimitedDays && rolloverType !== 'no' && location && (
        <>
          {enableRolloverExpiry && location?.resetQuotas === 'USER_YEAR' && (
            <Form.Item
              className="wrap-label"
              label={
                <>
                  <IntlMessages id={isFormInHours ? 'location.rolloverExpiryAfterHours' : 'location.rolloverExpiryAfterDays'} />
                  <Tooltip className="info-tooltip" title={<IntlMessages id="components.locationForm.daysInfo" />}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </>
              }
              extra={<IntlMessages id="components.locationForm.daysAfterEmploymentDate" />}
              name="rolloverExpiryAfterDays"
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
              {...formFullWidthLayout}
            >
              <InputNumber onChange={(value) => onChangeRolloverExpiryAfterDays(value as number)} style={{ width: 70 }} min={0} max={364} />
            </Form.Item>
          )}
        </>
      )}

      {!hasUnlimitedDays && rolloverType !== 'no' && location && (
        <>
          <Form.Item
            {...formFullWidthLayout}
            className="wrap-label"
            label={
              <>
                <IntlMessages id="app.negativeBroughtForward" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="app.negativeBroughtForwardInfo" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="negativeBroughtForwardAllowed"
          >
            <Radio.Group buttonStyle="solid" onChange={() => onChangeNegativeBroughtForward()}>
              <Radio.Button value={true}>
                <IntlMessages id="app.yes" />
              </Radio.Button>
              <Radio.Button value={false}>
                <IntlMessages id="app.no" />
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      <Divider style={isCreateLeaveTypeForm ? { opacity: 0.5, textTransform: 'capitalize' } : { textTransform: 'capitalize' }}>
        <IntlMessages id="app.timeOffInLieu" />
        {effectivePlan === SubscriptionPlanEnum.core && !hasToilFeatureFlag && (
          <Tooltip className="info-tooltip" title={<IntlMessages id="components.toil.featurePlan" />}>
            <InfoCircleOutlined />
          </Tooltip>
        )}
      </Divider>

      <Form.Item
        className="wrap-label"
        label={
          <>
            <IntlMessages id="components.toil.enableLabel" />
            <Tooltip className="info-tooltip" title={<IntlMessages id="components.toil.enableLabelTooltip" />}>
              <InfoCircleOutlined />
            </Tooltip>
          </>
        }
        valuePropName="checked"
        name="toil"
        {...formFullWidthLayout}
        style={isCreateLeaveTypeForm ? { opacity: 0.5 } : {}}
      >
        <FreemiumPricingSimpleTooltip isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium} onCorePlan={false}>
          <Radio.Group
            value={toil}
            buttonStyle="solid"
            disabled={isCreateLeaveTypeForm || (effectivePlan === SubscriptionPlanEnum.core && !hasToilFeatureFlag) || authCompany?.plan === SubscriptionPlanEnum.freemium}
            options={[
              { label: <IntlMessages id="app.yes" />, value: true },
              { label: <IntlMessages id="app.no" />, value: false },
            ]}
            onChange={onToggleToil}
            optionType="button"
          />
        </FreemiumPricingSimpleTooltip>
      </Form.Item>
      {isCreateLeaveTypeForm && (
        <Form.Item wrapperCol={{ span: 24 }}>
          <Text type="secondary">
            <IntlMessages id="components.leavePolicyForm.createLeaveTypeFormToilInfo1" />
            &nbsp;{effectivePlan === SubscriptionPlanEnum.core && <IntlMessages id="app.feature.access" />}
          </Text>
        </Form.Item>
      )}
      {(effectivePlan === 'Complete' || hasToilFeatureFlag) && toil && (
        <>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.toil.durationLabel" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.toil.durationLabelTooltip" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="toilShortestInterval"
            {...formFullWidthLayout}
          >
            <Select key="toilShortestInterval">
              {shortestLeaveIntervals.map((period) => (
                <Select.Option key={period} value={period}>
                  <IntlMessages id={`app.${period.toLowerCase()}`} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.toil.requester" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.toil.requesterTooltip" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            name="toilRequestsAllowedForUsers"
            {...formFullWidthLayout}
          >
            <Select key="toilRequestsAllowedForUsers">
              <Select.Option key={'true'} value={true}>
                <IntlMessages id={'components.toil.requesterAll'} />
              </Select.Option>
              <Select.Option key={'false'} value={false}>
                <IntlMessages id={'components.toil.requesterAdmin'} />
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            className="wrap-label"
            label={
              <>
                <IntlMessages id="components.toil.expirationLabel" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="components.toil.expirationLabelTooltip" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
            valuePropName="checked"
            name="toilExpiration"
            {...formFullWidthLayout}
          >
            <Radio.Group
              value={toilExpiration}
              buttonStyle="solid"
              options={[
                { label: <IntlMessages id="app.yes" />, value: true },
                { label: <IntlMessages id="app.no" />, value: false },
              ]}
              onChange={onToggleExpiration}
              optionType="button"
            />
          </Form.Item>
          {toilExpiration && (
            <Form.Item className="wrap-label" label={<IntlMessages id="components.toil.expiration" />} name="toilExpirationMonths" {...formFullWidthLayout}>
              <Select key="toilExpiration">
                <Select.Option key={'calendar-year'} value={'calendar-year'}>
                  <IntlMessages id={'components.toil.expirationDurationCalendar'} />
                </Select.Option>
                <Select.Option key={'1'} value={1}>
                  <IntlMessages id={'components.toil.expirationDuration'} values={{ noOfMonths: 1 }} />
                </Select.Option>
                <Select.Option key={'3'} value={3}>
                  <IntlMessages id={'components.toil.expirationDuration'} values={{ noOfMonths: 3 }} />
                </Select.Option>
                <Select.Option key={'6'} value={6}>
                  <IntlMessages id={'components.toil.expirationDuration'} values={{ noOfMonths: 6 }} />
                </Select.Option>
              </Select>
            </Form.Item>
          )}
        </>
      )}
    </>
  )
}
export default LeavePolicyForm
